import {useCallback, useEffect, useMemo, useState} from 'react';
import List from '@mui/material/List';
import {useSelector} from 'react-redux';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {useLocation, useNavigate} from 'react-router-dom';

import IconDashboard from '../../asset/icons/icon_menu_dashboard.svg';
import IconUserManagement from '../../asset/icons/icon_menu_usermanagement.svg';
import IconTutorialManagement from '../../asset/icons/icon_menu_tutorial.svg';
import IconOnboarding from '../../asset/icons/icon_menu_onboarding.svg';
import IconProcessing from '../../asset/icons/icon_menu_processing.svg';
import IconGlobalContent from '../../asset/icons/icon_menu_globalcontent.svg';
import IconGlobalControl from '../../asset/icons/icon_menu_gloablcontrol.svg';
import IconMenuDepartment from '../../asset/icons/icon_menu_department.svg';
import IconMusicSelection from '../../asset/icons/icon_menu_musicselection.svg';
import IconReports from '../../asset/icons/icon_menu_reports.svg';
import IconInternalStaff from '../../asset/icons/icon_menu_internalstaff.svg';
import IconNoPlay from '../../asset/icons/icon_menu_noplay.svg';
import IconOutstandingDownloads from '../../asset/icons/icon_menu_downloads.svg';
import IconReferral from '../../asset/icons/icons_referral.svg';
import IconTnnSponsor from '../../asset/icons/icon_menu_tnn.svg';
import IconAdd from '../../asset/icons/icon_addsongs.svg';
import IconKnowledgeBase from '../../asset/icons/icon_book_search.svg';
import IconAlert from '../../asset/icons/alert.svg';
import IconPartnerDashboard from '../../asset/icons/partner_dashboard.svg';
import InputSearch from '../styles/InputSearch';
import {BM_STINGER_REPO_MENU_SCOPES} from '../../pages/admin/global-content/bm-stinger-repo/permissionScopes';
import {ONBOARDING_WORKFLOW_MENU_SCOPES} from '../../pages/onboarding/onboardingPermssionScope';
import {scopesSelector} from '../../store/Slices/userSlice';
import {Box} from '@mui/material';

interface SubMenuItem {
  path: string;
  name: string;
  startIcon?: string;
  scopes: string[];
  disabled?: boolean;
}

interface MenuItem {
  path?: string;
  name: string;
  startIcon: string;
  scopes: string[];
  disabled?: boolean;
  subMenu?: SubMenuItem[];
}

const musicSectionScopes = ['is-admin', 'music-section:list'];
const noPlayScopes = ['is-admin', 'no-play:list'];
const genericStingerScopes = ['is-admin', 'generic-stinger:list'];
const networkEsmScopes = [
  'is-admin',
  'network-esm:list',
  'network-esm:create',
  'network-esm:update',
  'network-esm:delete',
];
const networkOdmScopes = [
  'is-admin',
  'network-odm:list',
  'network-odm:create',
  'network-odm:update',
  'network-odm:delete',
];
const genericBaseScopes = ['is-admin', 'generic-base-message:list'];
const commercialScopes = ['is-admin', 'commercial:list'];
const wristbandScopes = [
  'is-admin',
  'wristband-color:list',
  'wristband-color:store',
];
const walkupPreAnnouncementScopes = [
  'is-admin',
  'walkup-pre-announcement:list',
  'walkup-pre-announcement:create',
  'walkup-pre-announcement:update',
  'walkup-pre-announcement-event:list',
  'walkup-pre-announcement-event:update',
];

const PreBuiltPlaylistScopes = ['is-admin', 'prebuilt-playlists:list'];
const bmStingerRepoScopes = BM_STINGER_REPO_MENU_SCOPES;

const globalContentMenuScopes = Array.from(
  new Set([
    ...musicSectionScopes,
    ...noPlayScopes,
    ...genericStingerScopes,
    ...networkEsmScopes,
    ...networkOdmScopes,
    ...genericBaseScopes,
    ...commercialScopes,
    ...wristbandScopes,
    ...walkupPreAnnouncementScopes,
    ...PreBuiltPlaylistScopes,
    ...bmStingerRepoScopes,
  ]),
);

const globalContentMenu = {
  name: 'Global Content',
  startIcon: IconGlobalContent,
  scopes: globalContentMenuScopes,
  subMenu: [
    {
      path: '/admin/global-content/music-section',
      name: 'Music Section',
      startIcon: IconMusicSelection,
      scopes: musicSectionScopes,
    },
    {
      path: '/admin/global-content/no-play',
      name: 'No Play',
      startIcon: IconNoPlay,
      scopes: noPlayScopes,
    },
    {
      path: '/admin/global-content/generic-stingers',
      name: 'Generic Stingers',
      scopes: genericStingerScopes,
    },
    {
      path: '/admin/global-content/network-esm',
      name: "Network ESM's",
      scopes: networkEsmScopes,
    },
    {
      path: '/admin/global-content/network-odm',
      name: "Network ODM's",
      scopes: networkOdmScopes,
    },
    {
      path: '/admin/global-content/generic-base',
      name: 'Generic Base Messages',
      scopes: genericBaseScopes,
    },
    {
      path: '/admin/global-content/commercial',
      name: "PSA's / Commercial",
      scopes: commercialScopes,
    },
    {
      path: '/admin/global-content/pre-built-playlist',
      name: 'Pre-built Playlists',
      scopes: PreBuiltPlaylistScopes,
    },
    {
      path: '/admin/global-content/wristband-messages',
      name: 'Wristband Messages',
      scopes: wristbandScopes,
    },
    {
      path: '/admin/global-content/pre-announcements',
      name: 'Pre Announcements',
      scopes: walkupPreAnnouncementScopes,
    },
    {
      path: '/admin/global-content/bm-stinger-repository',
      name: 'BM & Stinger Repository',
      scopes: bmStingerRepoScopes,
    },
  ],
};

export const partnerDashboardScopes = ['is-admin', 'partner-dashboard'];
export const processingCenterScopes = ['is-admin', 'processing-center:list'];
export const internalStaffScopes = [
  'is-admin',
  'internal-users:list',
  'internal-users:create',
  'internal-users:edit',
];
export const partnerUsersScopes = ['is-admin', 'partner-accounts:list'];
export const tutorialManagementScopes = [
  'is-admin',
  'tutorial:list',
  'tutorial:create',
  'tutorial:update',
  'tutorial:delete',
  'tutorial:update-status',
];
export const knowledgeBaseScopes = [
  'is-admin',
  'knowledge:list',
  'knowledge:create',
  'knowledge:update',
  'knowledge:delete',
];
export const outstandingDownloadsScopes = ['is-admin', 'music-section:list'];
export const referralManagementScopes = ['is-admin', 'referral:list'];

export const managementMenuScopes = Array.from(
  new Set([
    ...partnerDashboardScopes,
    ...processingCenterScopes,
    ...internalStaffScopes,
    ...partnerUsersScopes,
    ...tutorialManagementScopes,
    ...knowledgeBaseScopes,
    ...outstandingDownloadsScopes,
    ...referralManagementScopes,
  ]),
);

const managementMenu = {
  name: 'Management',
  startIcon: IconMenuDepartment,
  scopes: managementMenuScopes,
  subMenu: [
    {
      path: '/admin/management/partners',
      name: 'Partner Dashboard',
      startIcon: IconPartnerDashboard,
      scopes: partnerDashboardScopes,
    },

    {
      path: '/admin/management/internal-users',
      name: 'Internal Staff Management',
      startIcon: IconInternalStaff,
      scopes: internalStaffScopes,
    },
    {
      path: '/admin/management/partner-users',
      name: 'User Management',
      startIcon: IconUserManagement,
      scopes: partnerUsersScopes,
    },
    {
      path: '/admin/management/tutorial-management',
      name: 'Tutorial Management',
      startIcon: IconTutorialManagement,
      scopes: tutorialManagementScopes,
    },
    {
      path: '/admin/management/knowledge-base-management',
      name: 'Knowledge Base Management',
      startIcon: IconKnowledgeBase,
      scopes: knowledgeBaseScopes,
    },
    {
      path: '/admin/management/outstanding-downloads',
      name: 'Outstanding Downloads',
      startIcon: IconOutstandingDownloads,
      scopes: outstandingDownloadsScopes,
    },
    {
      path: '/admin/management/referral-management',
      name: 'Referral Management',
      startIcon: IconReferral,
      scopes: referralManagementScopes,
    },
    {
      path: '/admin/management/processing-center',
      name: 'Processing Center',
      startIcon: IconProcessing,
      scopes: processingCenterScopes,
    },
  ],
};

const manageProductScopes = ['is-admin', 'manage-product:list'];
const musicGenreScopes = ['is-admin', 'music-genre:list'];
const sportsManagementScopes = ['is-admin', 'events:list'];
const voicePersonnelScopes = ['is-admin', 'music-voice-personnel:list'];
const commercialTypeScopes = ['is-admin', 'commercial-type:list'];
const templatePatternScopes = [
  'is-admin',
  'template-pattern:list',
  'template-pattern:create',
  'template-pattern:update',
  'template-pattern:delete',
];
const messageCreatorScopes = ['is-admin', 'music-creator:list'];
const contentTransferPortalScopes = ['is-admin'];

const globalControlsMenuScopes = Array.from(
  new Set([
    ...manageProductScopes,
    ...musicGenreScopes,
    ...sportsManagementScopes,
    ...voicePersonnelScopes,
    ...commercialTypeScopes,
    ...templatePatternScopes,
    ...messageCreatorScopes,
  ]),
);

const globalControlsMenu = {
  name: 'Global Controls',
  startIcon: IconGlobalControl,
  scopes: globalControlsMenuScopes,
  subMenu: [
    {
      path: '/admin/global-controls/manage-product',
      name: 'Manage Product Genres',
      scopes: manageProductScopes,
    },
    {
      path: '/admin/global-controls/music-genre',
      name: 'Music Genre',
      scopes: musicGenreScopes,
    },
    {
      path: '/admin/global-controls/sports-management',
      name: 'Sports Management',
      scopes: sportsManagementScopes,
    },
    {
      path: '/admin/global-controls/music-voice-personnel',
      name: 'Manage Voice Personnel',
      scopes: voicePersonnelScopes,
    },
    {
      path: '/admin/commercial-type',
      name: 'Sponsor Commercial Categories',
      scopes: commercialTypeScopes,
    },
    {
      path: '/admin/global-controls/template-pattern',
      name: 'Template Pattern',
      scopes: templatePatternScopes,
    },
    {
      path: '/admin/global-controls/message-creator',
      name: 'Message Creators',
      scopes: messageCreatorScopes,
    },
    {
      path: '/admin/global-controls/content-transfer-portal',
      name: 'Content Transfer Portal',
      scopes: contentTransferPortalScopes,
    },
  ],
};

const withAdmin = (...scopes: string[]) => ['is-admin', ...scopes];

const musicReviewScopes = withAdmin();
const accountBrandingReportScopes = withAdmin();
const baseMessageReportScopes = withAdmin('base-message-voice-report:list');
const esmOdmReportScopes = withAdmin('esm-odm-report:list');
const goneSilentReportScopes = withAdmin('gone-silent-report:list');
const copyrightReportScopes = withAdmin('copy-writer-report:list');
const partnerFinancialInformationScopes = withAdmin(
  'financial-information:generate-report',
);
const partnerActivityReportScopes = withAdmin(
  'partner-activity-report:download',
);
const advertiserReportScopes = withAdmin('advertiser-report:list');
const reportsMenuScopes = Array.from(
  new Set([
    ...musicReviewScopes,
    ...accountBrandingReportScopes,
    ...baseMessageReportScopes,
    ...esmOdmReportScopes,
    ...goneSilentReportScopes,
    ...copyrightReportScopes,
    ...partnerFinancialInformationScopes,
    ...partnerActivityReportScopes,
    ...advertiserReportScopes,
  ]),
);

const reportsMenu = {
  name: 'Reports',
  startIcon: IconReports,
  scopes: reportsMenuScopes,
  subMenu: [
    {
      path: '/admin/reports/music-review',
      name: 'Music Review',
      scopes: musicReviewScopes,
    },
    {
      path: '/admin/reports/account-branding-report',
      name: 'Missing Account branding',
      scopes: accountBrandingReportScopes,
    },
    {
      path: '/admin/reports/base-message-report',
      name: 'Base Message Report',
      scopes: baseMessageReportScopes,
    },
    {
      path: '/admin/reports/esm-odm-report',
      name: 'ESM/ODM Report',
      scopes: esmOdmReportScopes,
    },
    {
      path: '/admin/reports/gone-silent-report',
      name: 'Gone Silent Report',
      scopes: goneSilentReportScopes,
    },
    {
      path: '/admin/reports/copyright-report',
      name: 'Copyright Report',
      scopes: copyrightReportScopes,
    },
    {
      path: '/admin/reports/partner-financial-information',
      name: 'Partner Financial Information',
      scopes: partnerFinancialInformationScopes,
    },
    {
      path: '/admin/reports/partner-activity-report',
      name: 'Partner Activity Report',
      scopes: partnerActivityReportScopes,
    },
    {
      path: '/admin/reports/advertiser-report',
      name: 'Advertiser Report',
      scopes: advertiserReportScopes,
    },
  ],
};

const HeaderAdminMenu = ({handleClick}: {handleClick: () => void}) => {
  const scopes = useSelector(scopesSelector);
  const navigate = useNavigate();
  const location = useLocation();
  const [menu, setMenu] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const matchesSearch = useCallback(
    (text?: string) =>
      normalizedSearch.length === 0 ||
      (text ?? '').toLowerCase().includes(normalizedSearch),
    [normalizedSearch],
  );

  const pages = useMemo(() => {
    const computeDisabledState = (pageScopes: string[]) => {
      return !pageScopes.some(s => scopes.includes(s));
    };

    return [
      {
        path: '/',
        name: 'Dashboard',
        startIcon: IconDashboard,
        scopes: [
          'is-admin',
          'internal-users:list',
          'internal-users:create',
          'internal-users:edit',
        ],
      },

      {
        path: '/admin/alerts',
        name: 'Alerts',
        startIcon: IconAlert,
        scopes: ['is-admin', 'partner-alerts'],
      },
      {
        path: '/admin/status-management',
        name: 'Status Management',
        startIcon: IconReports,
        scopes: [
          'is-admin',
          'sponsor-commercial-task-status:list',
          'music-review-task-status:list',
          'messaging-task-status:list',
          'onboarding-task-status:list',
        ],
      },
      {
        path: '/admin/division-department',
        name: 'Division & Department',
        startIcon: IconMenuDepartment,
        scopes: ['is-admin'],
      },
      {
        path: '/partner/import',
        name: 'Onboarding',
        startIcon: IconOnboarding,
        scopes: ONBOARDING_WORKFLOW_MENU_SCOPES,
      },

      {...globalContentMenu},
      {...globalControlsMenu},
      {...managementMenu},

      {
        path: '/admin/tnn/control',
        name: 'TNN',
        startIcon: IconTnnSponsor,
        scopes: [
          'is-admin',
          'tnn-sponsor:list',
          'tnn-sponsor:create',
          'tnn-sponsor:show',
          'tnn-sponsor-campaign:create',
          'tnn-sponsor-campaign:list',
          'tnn-participate-partner:list',
          'tnn-dynamic-rate-card:campaign-list',
        ],
      },
      {
        path: '/admin/download-management',
        name: 'Download Management',
        startIcon: IconReports,
        scopes: ['is-admin'],
      },
      {...reportsMenu},
      {
        path: '/admin/demo-stinger',
        name: 'Demo Requests',
        startIcon: IconAdd,
        scopes: ['is-admin', 'demo-stinger-fulfillment:list'],
      },
    ]
      .map((page: MenuItem) => ({
        ...page,
        disabled: computeDisabledState(page.scopes),
        subMenu: page.subMenu
          ?.map((subPage: SubMenuItem) => ({
            ...subPage,
            disabled: computeDisabledState(subPage.scopes),
          }))
          .filter(subPage => !subPage.disabled),
      }))
      .filter(page => {
        // Existing scope filter: drop disabled parents with no remaining
        // accessible submenu items.
        if (!page.disabled || Boolean(page.subMenu?.length)) {
          // Fall through to the search filter below.
        } else {
          return false;
        }
        if (normalizedSearch.length === 0) {
          return true;
        }
        if (matchesSearch(page.name) || matchesSearch(page.path)) {
          return true;
        }
        if (
          page.subMenu?.some(
            subPage =>
              matchesSearch(subPage.name) || matchesSearch(subPage.path),
          )
        ) {
          return true;
        }
        return false;
      })
      .map((page: MenuItem & {subMenu?: SubMenuItem[]}) => {
        // When a search is active and the parent name does not match but a
        // submenu does, narrow the submenu to only matching entries so the
        // user sees exactly what they searched for.
        if (
          normalizedSearch.length > 0 &&
          !matchesSearch(page.name) &&
          !matchesSearch(page.path)
        ) {
          return {
            ...page,
            subMenu: page.subMenu?.filter(
              subPage =>
                matchesSearch(subPage.name) || matchesSearch(subPage.path),
            ),
          };
        }
        return page;
      });
  }, [scopes, matchesSearch, normalizedSearch]);

  const handleMenu = (name: string, path?: string) => {
    setMenu(prev => {
      if (prev === name) {
        return '';
      }
      return name;
    });
    if (path) {
      navigate(path);
      handleClick();
    }
  };

  const handleSubMenu = (menuName: string, path: string) => {
    setMenu(menuName);
    navigate(path);
    handleClick();
  };

  useEffect(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    // While a search is active, auto-expand any parent whose submenu
    // contains matches so the user immediately sees the filtered results.
    if (normalizedSearch.length > 0) {
      for (const page of pages) {
        if (
          page.subMenu &&
          page.subMenu.length > 0 &&
          page.subMenu.some(
            subPage =>
              matchesSearch(subPage.name) || matchesSearch(subPage.path),
          )
        ) {
          setMenu(page.name);
        }
      }
      return;
    }

    // No search: derive the open menu from the current URL. If no submenu
    // matches the URL, collapse everything (so clearing the search always
    // closes any menu that the search had auto-expanded).
    let matched = false;
    for (const page of pages) {
      if (page.subMenu) {
        const matchedSubPage = page.subMenu.find(
          subPage => location.pathname === subPage.path,
        );
        if (matchedSubPage) {
          setMenu(page.name);
          matched = true;
          break;
        }
      }
    }
    if (!matched) {
      setMenu('');
    }
  }, [location.pathname, matchesSearch, pages, searchTerm]);

  return (
    <List className='admin-menu scrollbar overflow-auto py-5' component='nav'>
      <Box className='px-6 pt-3 pb-3'>
        <InputSearch
          placeholder='Search menu'
          value={searchTerm}
          onChange={setSearchTerm}
        />
      </Box>
      {pages.map(page => {
        return (
          <>
            <ListItemButton
              key={page.path}
              onClick={() => handleMenu(page.name, page.path)}
              className={`py-3 pl-11 pr-6`}
              disabled={page.disabled}
              sx={{
                background:
                  location.pathname === page.path ||
                  page.subMenu?.some(
                    subPage => location.pathname === subPage.path,
                  )
                    ? 'var(--blue-variant7)!important'
                    : '',
              }}>
              <ListItemIcon>
                <img
                  src={page.startIcon}
                  alt='menu icon'
                  className='w-20 h-20 mr-4'
                />
              </ListItemIcon>
              <ListItemText primary={page.name} />
              {page.subMenu ? (
                menu === page.name ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )
              ) : null}
            </ListItemButton>
            {page.subMenu && (
              <Collapse in={menu === page.name} timeout='auto' unmountOnExit>
                <List
                  disablePadding
                  sx={{background: 'var(--blue-variant10) !important'}}>
                  {page.subMenu.map((subPage, subIndex) => (
                    <ListItemButton
                      key={subIndex}
                      className={`py-3 pl-25 pr-6`}
                      disabled={subPage.disabled}
                      sx={{
                        background:
                          location.pathname === subPage.path
                            ? 'var(--blue-variant7)!important'
                            : '',
                      }}
                      onClick={() => handleSubMenu(page.name, subPage.path)}>
                      <ListItemText
                        primary={
                          <span>
                            <span className='mr-4'>-</span> {subPage.name}
                          </span>
                        }
                        className='text-white-50'
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </>
        );
      })}
    </List>
  );
};

export default HeaderAdminMenu;
